<template>
  <div class="leftCardContainer">
    <div class="rightSideTitleActions">
      <span class="leftTitle">{{ $tc("Map", 2) }}</span>
      <div class="actionsContainer">
        <div class="searchContainer">
          <v-icon> search </v-icon>
          <InputField
            inputType="text"
            :value="search"
            :placeholder="$t('Search')"
            :inputStyle="{
              borderRadius: '0px',
              width: '130px',
              borderTop: 'none',
              borderRight: 'none',
              borderLeft: 'none',
              borderBottom: '1px solid rgba(118, 118, 118, 0.50)',
            }"
            @input="handleSearch($event)"
          />
        </div>
        <CustomButton
          icon="delete"
          :width="'20px'"
          :height="'20px'"
          :iconSize="12"
          :disabled="!selectedObjectID || !userHasPermission('consumable_destroy')"
          :buttonStyling="{
            padding: '8px',
            marginRight: '5px',
            borderRadius: '4px',
          }"
          @buttonClick="openDeleteDialog"
        />
        <CustomButton
          icon="file_download"
          :width="'20px'"
          :height="'20px'"
          :iconSize="12"
          :disabled="false"
          :buttonStyling="{
            padding: '8px',
            marginRight: '5px',
            borderRadius: '4px',
          }"
          @buttonClick="downloadConsumables"
        />
        <CustomButton
          icon="add"
          :width="'20px'"
          :height="'20px'"
          :iconSize="12"
          :buttonStyling="{
            padding: '8px',
            marginRight: '5px',
            borderRadius: '4px',
          }"
          :disabled="!userHasPermission('consumable_store') || userIsCreatingOrEditingData"
          @buttonClick="handleAdd"
        />
      </div>
    </div>
    <div class="tableDivContainer">
      <Table
        :checkbox="false"
        :loading="loading"
        :headers="headers"
        :items="mapsProcessed"
        :sortable="true"
        @onScrollBottom="onScrollBottom"
        @onScrollTop="onScrollTop"
        @sortClick="onSortClick"
        :emitRowClickBusEvent="true"
      />
    </div>
    <dialog-choice
      :isOpen="deleteDialogIsOpen"
      :title="$t('DeleteMap')"
      :content="$t('DeleteMapConfirmation')"
      :mainActionLabel="$t('Confirm')"
      @mainAction="handleDelete"
      :secondaryActionLabel="$t('Cancel')"
      @secondaryAction="deleteDialogIsOpen = false"
      icon="mdi-delete"
      iconColor="var(--app-primary-base)"
    />
    <loading-spinner
      :active.sync="loading"
      :is-full-page="false"
      color="var(--app-primary-base)"
      style="position: absolute"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { bus } from "@/main.js";
import { managementDatatableMixin } from "@/mixins/management/managementDatatableMixin";
import ManagementPage from "@/newComponents/_Organisms/ManagementPage.vue";
import Table from "@/newComponents/_Molecules/Table.vue";
import CustomButton from "@/newComponents/_Atoms/CustomButton.vue";
import InputField from "@/newComponents/_Atoms/InputField.vue";
import LoadingSpinner from "vue-loading-overlay";
import DialogChoice from "@/newComponents/_Molecules/DialogChoice.vue";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";

export default {
  name: "TableView",

  mixins: [managementDatatableMixin, checkPermissionsMixin],

  components: {
    Table,
    ManagementPage,
    CustomButton,
    InputField,
    LoadingSpinner,
    DialogChoice,
  },

  props: {
    selectedObjectID: {
      type: Number,
    },
  },

  data() {
    return {
      dialog: false,
      deleteDialogIsOpen: false,
      value: null,
      type: "normal",
      search: null,
      activeTab: 0,
      loading: false,
      scrollTop: 0,
      sortOrder: null,
      scrollHeight: 0,
      tabs: [],
      pageNumber: 1,
      pagination: 25,
      currentPageTop: 1,
      currentPageBot: 1,
      lastPage: 2,
      maps: [],
      permissionName: "consumable",
      userIsCreatingOrEditingData: false,
    };
  },

  mounted() {
    this.getInitialMaps();
    bus.$on("reload-maps", () => this.getInitialMaps());
    bus.$on("toggle-create-edit-flag", () => this.userIsCreatingOrEditingData = !this.userIsCreatingOrEditingData)
  },

  destroyed() {
    bus.$off("reload-maps");
    bus.$off("toggle-create-edit-flag");
  },

  computed: {
    headers() {
      return [
        { text: this.$i18n.tc("Label", 1), varName: "code" },
        { text: this.$i18n.t("Active"), varName: "active" },
        { text: this.$i18n.t("Index"), varName: "index" },
      ];
    },
    mapsProcessed() {
      let maps = [];
      this.maps.forEach((mapElement) => {
        let map = mapElement;
        map.active = map.active ? "Yes" : "No";
        maps.push(map);
      });
      return maps;
    },
  },

  methods: {
    ...mapActions(["deleteConsumables", "fetchAllMaps","downloadAllConsumables"]),

    closeDialog() {
      this.dialog = false;
    },

    async getInitialMaps() {
      this.loading = true;
      this.currentPageBot = 1;
      this.currentPageTop = 1;
      let queryParams = {
        "page[number]": 1,
        paginate: 25,
      };

      if (this.search !== null) {
        queryParams["filter[search]"] = this.search;
      }

      if (this.sortOrder !== null) {
        queryParams["sort"] = this.sortOrder;
      }

      this.getMaps(queryParams);
    },

    getMapsBottom() {
      this.loading = true;
      this.pagination = 25;
      if (this.maps.length >= 75) {
        this.currentPageTop++;
      }
      this.currentPageBot++;

      let queryParams = {
        "page[number]": this.currentPageBot,
        paginate: this.pagination,
      };

      if (this.search !== null) {
        queryParams["filter[search]"] = this.search;
      }

      if (this.sortOrder !== null) {
        queryParams["sort"] = this.sortOrder;
      }

      this.getMaps(queryParams, "bottom");
    },

    async getMapsTop() {
      this.loading = true;
      this.pagination = 25;
      this.currentPageTop--;

      let queryParams = {
        "page[number]": this.currentPageTop,
        paginate: this.pagination,
      };

      if (this.search !== null) {
        queryParams["filter[search]"] = this.search;
      }

      if (this.sortOrder !== null) {
        queryParams["sort"] = this.sortOrder;
      }

      this.getMaps(queryParams, "top");
    },

    getMaps(queryParams, scrollDirection = null) {
      this.getAllMaps(queryParams, scrollDirection);
    },

    async getAllMaps(queryParams = null, scrollDirection = null) {
      this.loading = true;
      try {
        let maps = await this.fetchAllMaps(queryParams);
        this.processMapsData(maps, scrollDirection);
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
        this.$nextTick(() =>
          this.updateLocalPagination(this.paginationFromParent)
        );
      }
    },

    processMapsData(mapsData, scrollDirection = null) {
      let allMaps = null;
      let currentScroll = null;
      let rowElement = null;
      switch (scrollDirection) {
        case "top":
          this.lastPage = mapsData.meta.last_page;
          allMaps = [...mapsData.data, ...this.maps];
          this.maps = Array.from(new Set(allMaps.map((a) => a.sm_id))).map(
            (id) => {
              return allMaps.find((a) => a.sm_id === id);
            }
          );
          if (this.maps.length > 75) {
            this.maps.splice(-(this.maps.length - 75));
            this.currentPageBot--;
          }

          currentScroll = document.getElementById("scroll-bar").scrollTop;
          this.loading = false;

          rowElement = document.getElementById("rowId");
          if (rowElement) {
            const rowHeight = rowElement.clientHeight;
            for (let i = 0; i < 25; i++) {
              const offset = rowHeight * i;
              document.getElementById("scroll-bar").scrollTo({
                top: currentScroll + offset,
                behavior: "smooth",
              });
            }
          }
          break;
        case "bottom":
          this.lastPage = mapsData.meta.last_page;
          allMaps = [...this.maps, ...mapsData.data];
          this.maps = Array.from(new Set(allMaps.map((a) => a.sm_id))).map(
            (id) => {
              return allMaps.find((a) => a.sm_id === id);
            }
          );
          if (this.maps.length > 75) {
            this.maps = this.maps.slice(this.maps.length - 75);
          }

          currentScroll = document.getElementById("scroll-bar").scrollTop;
          this.loading = false;

          rowElement = document.getElementById("rowId");
          if (rowElement) {
            const rowHeight = rowElement.clientHeight;
            for (let i = 0; i < 2; i++) {
              const offset = rowHeight * i;
              document.getElementById("scroll-bar").scrollTo({
                top: currentScroll - offset,
                behavior: "smooth",
              });
            }
          }
          break;
        default:
          this.maps = mapsData.data;
          this.lastPage = mapsData.meta.last_page;
          break;
      }
    },

    handleSearch: _.debounce(function (event) {
      if (event === "") {
        this.search = null;
      } else {
        this.search = event;
      }
      this.maps = [];
      this.getInitialMaps();
    }, 1000),

    onScrollBottom() {
      if (this.currentPageBot !== this.lastPage) {
        this.pageNumber++;
        this.getMapsBottom();
      }
    },

    onScrollTop() {
      if (this.currentPageTop !== 1) {
        this.pageNumber--;
        this.getMapsTop();
      }
    },

    onSortClick(sortOrder, index) {
      this.maps = [];
      if (sortOrder === "asc") {
        this.sortOrder = this.headers[index].varName;
      } else if (sortOrder === "desc") {
        this.sortOrder = `-${this.headers[index].varName}`;
      } else {
        this.sortOrder = sortOrder;
      }
      this.getInitialMaps();
    },

    handleAdd() {
      this.$emit("open-create-map-form");
    },

    openDeleteDialog() {
      if (this.selectedObjectID) {
        this.deleteDialogIsOpen = true;
      }
    },

    async handleDelete() {
      try {
        this.loading = true;
        await this.deleteConsumables(this.selectedObjectID);
        this.getInitialMaps();
        bus.$emit("clear-SelectedItems");
        bus.$emit("clear-selected-row");
        this.$emit("clear-selected-item");
      } catch (error) {
        this.$toasted.global.toast_error(error.response.data.data);
      } finally {
        this.deleteDialogIsOpen = false;
        this.loading = false;
      }
    },

    async downloadConsumables(){
      try {
        this.loading = true;
        let data = await this.downloadAllConsumables();
        window.open(data.file_url_public);
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    }
  },
};
</script>

<style scoped>
.searchContainer {
  display: flex;
  align-items: center;
}

.tableDivContainer {
  height: 87%;
  margin-top: 5px;
}

.leftCardContainer {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.25);
  padding: 20px 28px;
}

.actionsContainer {
  display: flex;
  align-items: center;
  gap: 3px;
}

.leftTitle {
  color: #767676;
  font-family: Lato;
  font-size: 10px;
  font-style: normal;
  font-weight: 800;
  line-height: 20px;
  text-transform: uppercase;
}

.rightSideTitleActions {
  height: 5%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.leftCardContainer {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.25);
  padding: 20px 28px;
}

.actionsContainer {
  display: flex;
  align-items: center;
  gap: 3px;
}

.leftTitle {
  color: #767676;
  font-family: Lato;
  font-size: 10px;
  font-style: normal;
  font-weight: 800;
  line-height: 20px;
  /* 200% */
  text-transform: uppercase;
}

.rightSideTitleActions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.table-custom {
  height: 90%;
}

.table-buttons-custom {
  height: 70%;
}
</style>



// WEBPACK FOOTER //
// src/newComponents/Maps/TableView.vue